import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpLink } from "@trpc/client";
import { createTRPCReact } from "@trpc/react-query";
import type { inferRouterInputs, inferRouterOutputs } from "@trpc/server";
import type { AppRouter } from "@voidpulse/api";
import type { ReactNode } from "react";
import { toast } from "react-toastify";

export type RouterInput = inferRouterInputs<AppRouter>;
export type RouterOutput = inferRouterOutputs<AppRouter>;

export type DbChart = RouterOutput["getCharts"]["charts"][0];
/** getReport output for metric-based reports (SQL reports return rows instead of datas). */
export type ReportDatas = Extract<RouterOutput["getReport"], { datas: unknown[] }>["datas"];
export type SqlReportData = Extract<RouterOutput["getReport"], { rows: unknown[] }>;

export const apiUrl =
  process.env.NEXT_PUBLIC_API_URL ||
  (process.env.NODE_ENV === "production" ? "https://api.voidpulse.ai" : "http://localhost:4001");

let antiForgeryToken = "";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    },
  },
  queryCache: new QueryCache({
    onError: (error) => {
      toast.error(`An error occurred: ${error instanceof Error ? error.message : "Unknown"}`);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      toast.error(`An error occurred: ${error instanceof Error ? error.message : "Unknown"}`);
    },
  }),
});

export const trpc = createTRPCReact<AppRouter>();

export const trpcClient = trpc.createClient({
  links: [
    httpLink({
      url: `${apiUrl}/trpc`,
      async fetch(url, options) {
        const headers = new Headers(options?.headers);
        if (antiForgeryToken) {
          headers.set("x-voidpulse-anti-forgery", antiForgeryToken);
        }
        const response = await fetch(url, {
          ...options,
          headers,
          credentials: "include",
        });
        antiForgeryToken = response.headers.get("x-voidpulse-anti-forgery") || antiForgeryToken;
        return response;
      },
    }),
  ],
});

export function TRPCProvider({ children }: { children: ReactNode }) {
  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </trpc.Provider>
  );
}
